import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import "./MobileFiltersDrawer.css";

const MILEAGE_VARIANCES = [2, 5, 10, 15, 20];
const RADIUS_OPTIONS_ACTIVE = [25, 50, 100, 250, 500];
const RADIUS_OPTIONS_SOLD = [25, 50, 100];
const MIN_MILEAGE = 0;
const MAX_MILEAGE = 500000;

function formatMileageRange([min, max]) {
  const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n));
  return `${fmt(min)} — ${fmt(max)} mi`;
}

function applySimilarMileageRange(hooptieMileage, pct) {
  if (!hooptieMileage) return [MIN_MILEAGE, MAX_MILEAGE];
  const low = Math.max(0, Math.floor(hooptieMileage * (1 - pct / 100)));
  const high = Math.ceil(hooptieMileage * (1 + pct / 100));
  return [low, high];
}

export default function MobileFiltersDrawer({
  open,
  onClose,
  filters,
  onFilterChange,
  hooptieMileage,
  filteredCount,
  defaultFilters,
  isSoldComps = false,
}) {
  const [similarMileagePct, setSimilarMileagePct] = useState(15);
  const RADIUS_OPTIONS = isSoldComps ? RADIUS_OPTIONS_SOLD : RADIUS_OPTIONS_ACTIVE;

  useEffect(() => {
    if (!open) return;
    if (filters.similarMileage && hooptieMileage && filters.mileageRange?.length === 2) {
      const [, max] = filters.mileageRange;
      const pct = max > 0 ? Math.round(((max / hooptieMileage) - 1) * 100) : 15;
      const clamped = MILEAGE_VARIANCES.includes(pct) ? pct : 15;
      setSimilarMileagePct(clamped);
    } else {
      setSimilarMileagePct(15);
    }
  }, [open, filters.similarMileage, hooptieMileage, filters.mileageRange]);

  if (!open) return null;

  const mileageRange = filters.mileageRange ?? [MIN_MILEAGE, MAX_MILEAGE];
  const radius = RADIUS_OPTIONS.includes(filters.radius) ? filters.radius : 100;

  const handleMileageChange = (index, value) => {
    const val = parseInt(value, 10);
    const newRange = [...mileageRange];
    newRange[index] = val;
    if (index === 0 && newRange[0] > newRange[1]) newRange[1] = newRange[0];
    if (index === 1 && newRange[1] < newRange[0]) newRange[0] = newRange[1];
    onFilterChange({ ...filters, mileageRange: newRange, similarMileage: false });
  };

  const handleSimilarMileageToggle = () => {
    const newSimilar = !filters.similarMileage;
    if (newSimilar && hooptieMileage) {
      const newRange = applySimilarMileageRange(hooptieMileage, similarMileagePct);
      onFilterChange({ ...filters, similarMileage: true, mileageRange: newRange });
    } else {
      onFilterChange({ ...filters, similarMileage: false, mileageRange: [MIN_MILEAGE, MAX_MILEAGE] });
    }
  };

  const handleMileagePctChange = (pct) => {
    setSimilarMileagePct(pct);
    if (filters.similarMileage && hooptieMileage) {
      const newRange = applySimilarMileageRange(hooptieMileage, pct);
      onFilterChange({ ...filters, mileageRange: newRange });
    }
  };

  const snapRadius = (val) => {
    const n = Number(val);
    return RADIUS_OPTIONS.reduce((prev, curr) =>
      Math.abs(curr - n) < Math.abs(prev - n) ? curr : prev
    );
  };
  const radiusMin = RADIUS_OPTIONS[0];
  const radiusMax = RADIUS_OPTIONS[RADIUS_OPTIONS.length - 1];

  const handleRadiusChange = (mi) => {
    onFilterChange({ ...filters, radius: typeof mi === "number" ? mi : snapRadius(mi) });
  };

  const handleDealerType = (type) => {
    onFilterChange({
      ...filters,
      independentDealersOnly: type === "independent",
      franchiseOnly: type === "franchise",
    });
  };

  const handleToggle = (key) => {
    const updates = {
      matchingVersion: "matchingVersionOnly",
      matchingTransmission: "matchingTransmissionOnly",
      matchingDrivetrain: "matchingDrivetrainOnly",
    };
    const filterKey = updates[key];
    onFilterChange({ ...filters, [filterKey]: !filters[filterKey] });
  };

  const handleClear = () => {
    onFilterChange({ ...defaultFilters });
  };

  const dealerType = filters.franchiseOnly ? "franchise" : filters.independentDealersOnly ? "independent" : "all";

  const overlayContent = (
    <div className="mfd-overlay" onClick={onClose}>
      <div className="mfd-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="mfd-header">
          <button type="button" className="mfd-close" onClick={onClose} aria-label="Close">
            <CloseOutlined />
          </button>
          <h2 className="mfd-title">Filters</h2>
          <button type="button" className="mfd-clear" onClick={handleClear}>
            Clear
          </button>
        </header>

        <div className="mfd-body">
          <div className="mfd-divider" />

          {/* MARKET SCOPE */}
          <section className="mfd-section">
            <h3 className="mfd-section-title">MARKET SCOPE</h3>

            <div className="mfd-block">
              <label className="mfd-field-label">Radius</label>
              <div className="mfd-radius-value">[ {radius} mi ]</div>
              <div className="mfd-radius-slider-wrap">
                <input
                  type="range"
                  min={radiusMin}
                  max={radiusMax}
                  value={radius}
                  onChange={(e) => handleRadiusChange(snapRadius(e.target.value))}
                  className="mfd-radius-slider"
                />
              </div>
              <div className="mfd-radius-pills">
                {RADIUS_OPTIONS.map((mi) => (
                  <button
                    key={mi}
                    type="button"
                    className={`mfd-pill ${radius === mi ? "active" : ""}`}
                    onClick={() => handleRadiusChange(mi)}
                  >
                    {mi}
                  </button>
                ))}
              </div>
            </div>

            <div className="mfd-block">
              <label className="mfd-field-label">Dealer Type</label>
              <div className="mfd-dealer-segmented">
                <button
                  type="button"
                  className={`mfd-segmented-btn ${dealerType === "all" ? "active" : ""}`}
                  onClick={() => handleDealerType("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`mfd-segmented-btn ${dealerType === "independent" ? "active" : ""}`}
                  onClick={() => handleDealerType("independent")}
                >
                  Independent
                </button>
                <button
                  type="button"
                  className={`mfd-segmented-btn ${dealerType === "franchise" ? "active" : ""}`}
                  onClick={() => handleDealerType("franchise")}
                >
                  Franchise
                </button>
              </div>
            </div>
          </section>

          <div className="mfd-divider" />

          {/* MATCHING */}
          <section className="mfd-section">
            <h3 className="mfd-section-title">MATCHING</h3>

            <div className="mfd-block">
              <label className="mfd-field-label">Mileage</label>
              <div className="mfd-range-text">{formatMileageRange(mileageRange)}</div>
              <div
                className="mfd-slider-container"
                style={{
                  "--min-val": mileageRange[0],
                  "--max-val": mileageRange[1],
                  "--min-bound": MIN_MILEAGE,
                  "--max-bound": MAX_MILEAGE,
                }}
              >
                <div className="mfd-slider-track">
                  <input
                    type="range"
                    min={MIN_MILEAGE}
                    max={MAX_MILEAGE}
                    value={mileageRange[0]}
                    onChange={(e) => handleMileageChange(0, e.target.value)}
                    className="mfd-slider mfd-slider-min"
                  />
                  <input
                    type="range"
                    min={MIN_MILEAGE}
                    max={MAX_MILEAGE}
                    value={mileageRange[1]}
                    onChange={(e) => handleMileageChange(1, e.target.value)}
                    className="mfd-slider mfd-slider-max"
                  />
                </div>
              </div>
            </div>

            <div className="mfd-block mfd-toggle-row">
              <span className="mfd-toggle-label">Match Similar Mileage</span>
              <label className="mfd-switch">
                <input
                  type="checkbox"
                  checked={!!filters.similarMileage}
                  onChange={handleSimilarMileageToggle}
                />
                <span className="mfd-switch-slider" />
              </label>
            </div>
            <div className="mfd-tolerance-row">
              <span className="mfd-field-label">Tolerance</span>
              <select
                className="mfd-select"
                value={similarMileagePct}
                onChange={(e) => handleMileagePctChange(Number(e.target.value))}
                aria-label="Mileage variance percent"
              >
                {MILEAGE_VARIANCES.map((p) => (
                  <option key={p} value={p}>±{p}%</option>
                ))}
              </select>
            </div>

            <div className="mfd-block mfd-toggle-row">
              <span className="mfd-toggle-label">Match Version</span>
              <label className="mfd-switch">
                <input
                  type="checkbox"
                  checked={!!filters.matchingVersionOnly}
                  onChange={() => handleToggle("matchingVersion")}
                />
                <span className="mfd-switch-slider" />
              </label>
            </div>
            <div className="mfd-block mfd-toggle-row">
              <span className="mfd-toggle-label">Match Drivetrain</span>
              <label className="mfd-switch">
                <input
                  type="checkbox"
                  checked={!!filters.matchingDrivetrainOnly}
                  onChange={() => handleToggle("matchingDrivetrain")}
                />
                <span className="mfd-switch-slider" />
              </label>
            </div>
            <div className="mfd-block mfd-toggle-row">
              <span className="mfd-toggle-label">Match Transmission</span>
              <label className="mfd-switch">
                <input
                  type="checkbox"
                  checked={!!filters.matchingTransmissionOnly}
                  onChange={() => handleToggle("matchingTransmission")}
                />
                <span className="mfd-switch-slider" />
              </label>
            </div>
          </section>

          <div className="mfd-divider" />
        </div>

        <footer className="mfd-footer">
          <button type="button" className="mfd-apply" onClick={onClose}>
            <SearchOutlined />
            View {filteredCount} Listings
          </button>
        </footer>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlayContent, document.body);
}
